import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useSalon } from "../lib/salon";
import { formatTime, formatDateTime, isoDate, naiveISO, isSameDay, hoursMinutes, isoOrder } from "../lib/time";
import { Button } from "../components/ui/button";
import { CaretLeft, CaretRight, Plus, X, Check, ArrowsClockwise } from "@phosphor-icons/react";
import BookingModal from "../components/BookingModal";
import BookingBlock from "../components/BookingBlock";
import RescheduleModal from "../components/RescheduleModal";
import { assignLanes, stageSegments, isMultiStage, occupiedMinutes } from "../lib/calendarLayout";
import {
  SNAP_MIN,
  DRAG_THRESHOLD_PX,
  crossedDragThreshold,
  isDraggable,
  resolveSnapMinutes,
  formatDropIso,
  legalityHintOverOccupied,
} from "../lib/calendarDrag";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useRefresh } from "../context/RefreshContext";

const HOUR_PX = 64; // 1 hour height
const DAY_START_HOUR = 8;
const DAY_END_HOUR = 21;
const HOURS = DAY_END_HOUR - DAY_START_HOUR;

function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function fmtDate(d) { return isoDate(d); }
const fmtTime = formatTime;

function minutesFromDayStart(iso) {
  const { hour, minute } = hoursMinutes(iso);
  return (hour - DAY_START_HOUR) * 60 + minute;
}

export default function CalendarPage() {
  const [view, setView] = useState("day"); // "day" | "week"
  const [date, setDate] = useState(startOfDay(new Date()));
  const [stylists, setStylists] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const { bookingsVersion } = useRefresh();
  const salon = useSalon();
  const currency = salon?.currency_symbol || "$";

  const range = useMemo(() => {
    if (view === "day") return [date, addDays(date, 1)];
    const day = date.getDay() === 0 ? 6 : date.getDay() - 1; // Mon=0
    const start = addDays(date, -day);
    return [start, addDays(start, 7)];
  }, [view, date]);

  const fetchData = async () => {
    const [s, sv, b] = await Promise.all([
      api.get("/stylists").then((r) => r.data),
      api.get("/services").then((r) => r.data),
      api.get("/bookings", { params: { start: naiveISO(range[0]), end: naiveISO(range[1]) } }).then((r) => r.data),
    ]);
    setStylists(s.filter((x) => x.active)); setServices(sv); setBookings(b);
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [view, date, bookingsVersion]);

  const cancelBooking = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking cancelled");
      setSelected(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const completeBooking = async (id) => {
    try {
      await api.post(`/bookings/${id}/complete`);
      toast.success("Marked as completed");
      setSelected(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Schedule</div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight font-light mt-2">
            {date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border rounded-md overflow-hidden" data-testid="view-toggle">
            <button
              onClick={() => setView("day")}
              data-testid="view-day-btn"
              className={`px-4 py-2 text-sm ${view === "day" ? "bg-primary text-primary-foreground" : "bg-white"}`}
            >Day</button>
            <button
              onClick={() => setView("week")}
              data-testid="view-week-btn"
              className={`px-4 py-2 text-sm ${view === "week" ? "bg-primary text-primary-foreground" : "bg-white"}`}
            >Week</button>
          </div>
          <Button variant="outline" size="icon" onClick={() => setDate(addDays(date, view === "day" ? -1 : -7))} data-testid="cal-prev-btn">
            <CaretLeft size={16} />
          </Button>
          <Button variant="outline" onClick={() => setDate(startOfDay(new Date()))} data-testid="cal-today-btn">Today</Button>
          <Button variant="outline" size="icon" onClick={() => setDate(addDays(date, view === "day" ? 1 : 7))} data-testid="cal-next-btn">
            <CaretRight size={16} />
          </Button>
          <Button
            onClick={() => { setDefaults({ date: fmtDate(date) }); setOpenNew(true); }}
            className="bg-primary hover:bg-[#2B4F39]"
            data-testid="cal-new-booking-btn"
          >
            <Plus size={16} className="mr-1" /> New booking
          </Button>
        </div>
      </div>

      {view === "day" ? (
        <DayView
          date={date}
          stylists={stylists}
          bookings={bookings}
          onSelect={setSelected}
          onMove={async ({ bookingId, startAtIso, stylistId, originalBooking }) => {
            const payload = { start_at: startAtIso };
            if (stylistId && stylistId !== originalBooking.stylist_id) {
              payload.stylist_id = stylistId;
            }
            // Skip the PUT entirely when nothing actually changed (e.g.
            // user drags 3 pixels then drops on the same slot). The
            // backend would no-op too, but saving the round-trip keeps
            // the calendar snappy and avoids spurious toasts.
            if (
              payload.start_at === originalBooking.start_at &&
              !payload.stylist_id
            ) {
              return;
            }
            try {
              await api.put(`/bookings/${bookingId}`, payload);
              toast.success("Booking moved");
              fetchData();
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail) || e.message);
              // Failure path: re-fetch to restore the booking to its
              // canonical position. No local mutation was ever
              // committed, so this is a no-op except in the rare case
              // the local cache drifted.
              fetchData();
            }
          }}
          onSlotClick={(stylistId, hour) => {
            const dt = new Date(date);
            dt.setHours(hour, 0, 0, 0);
            setDefaults({ date: fmtDate(date), stylistId });
            setOpenNew(true);
          }}
        />
      ) : (
        <WeekView
          start={range[0]}
          stylists={stylists}
          bookings={bookings}
          onSelect={setSelected}
          onDayClick={(d) => { setView("day"); setDate(d); }}
        />
      )}

      <BookingModal
        open={openNew}
        onOpenChange={setOpenNew}
        onCreated={fetchData}
        stylists={stylists}
        services={services}
        defaults={defaults}
      />

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-white" data-testid="booking-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Booking</DialogTitle>
            <DialogDescription>Details and actions</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <Row label="Customer" value={selected.customer?.name} sub={selected.customer?.phone} />
              <Row label="Service" value={selected.service?.name || selected.service_name_snapshot} sub={`${selected.duration_minutes} min · ${currency}${selected.price?.toFixed?.(0)}`} />
              <Row label="Stylist" value={selected.stylist?.name} />
              <Row label="When" value={`${formatDateTime(selected.start_at)} → ${fmtTime(selected.end_at)}`} />
              {isMultiStage(selected) && (
                <StageBreakdown booking={selected} currency={currency} />
              )}
              <div className="flex justify-between items-center py-2 border-b border-border last:border-0">
                <span className="overline">Status</span>
                <span
                  className={`inline-block px-2 py-1 rounded-md text-xs ${
                    selected.status === "scheduled" ? "bg-[#D1E0D7] text-[#1A3626]" :
                    selected.status === "completed" ? "bg-[#EFEBE3] text-foreground" :
                    selected.status === "cancelled" ? "bg-[#EADCD9] text-[#C46B54]" : ""
                  }`}
                  data-testid="booking-detail-status"
                >
                  {selected.status}
                </span>
              </div>
              {selected.notes && <Row label="Notes" value={selected.notes} />}
            </div>
          )}
          <DialogFooter>
            {selected?.status === "scheduled" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setRescheduling(selected); setSelected(null); }}
                  data-testid="reschedule-booking-btn"
                >
                  <ArrowsClockwise size={16} className="mr-1" /> Reschedule
                </Button>
                <Button
                  variant="outline"
                  onClick={() => completeBooking(selected.id)}
                  data-testid="complete-booking-btn"
                  className="border-[#1A3626] text-[#1A3626] hover:bg-[#D1E0D7]"
                >
                  <Check size={16} className="mr-1" /> Complete
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => cancelBooking(selected.id)}
                  data-testid="cancel-booking-btn"
                >
                  <X size={16} className="mr-1" /> Cancel
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RescheduleModal
        open={!!rescheduling}
        onOpenChange={(v) => !v && setRescheduling(null)}
        booking={rescheduling}
        stylists={stylists}
        onRescheduled={fetchData}
      />
    </div>
  );
}

function Row({ label, value, sub }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-border last:border-0">
      <span className="overline">{label}</span>
      <div className="text-right">
        <div className="font-medium">{value || "—"}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
}

function StageBreakdown({ booking }) {
  const segments = stageSegments(booking);
  const totalMin = segments.reduce((a, s) => a + s.durationMin, 0);
  const occMin = occupiedMinutes(booking);
  return (
    <div className="py-2 border-b border-border" data-testid="stage-breakdown">
      <div className="flex justify-between items-baseline mb-2">
        <span className="overline">Stages</span>
        <span className="text-[10px] font-mono text-muted-foreground">
          {occMin}m occupied of {totalMin}m total
        </span>
      </div>
      <ol className="space-y-1">
        {segments.map((s, i) => {
          const isProc = s.type === "processing";
          return (
            <li
              key={s.key}
              data-testid={`stage-row-${i}`}
              data-stage-type={s.type}
              className="flex items-center justify-between gap-3 px-2 py-1.5 rounded-md border"
              style={{
                background: isProc
                  ? "repeating-linear-gradient(135deg, rgba(209,224,215,0.35) 0 6px, rgba(209,224,215,0.10) 6px 12px)"
                  : "#D1E0D7",
                borderColor: isProc ? "#1A362633" : "#1A3626",
                borderStyle: isProc ? "dashed" : "solid",
              }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-[10px] tabular-nums text-[#1A3626]/70 w-4">
                  {i + 1}
                </span>
                <span className="font-medium text-sm truncate">{s.name}</span>
                <span
                  className={`text-[9px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded ${
                    isProc
                      ? "bg-white/70 text-[#1A3626]/70"
                      : "bg-[#1A3626] text-white"
                  }`}
                >
                  {s.type}
                </span>
              </div>
              <span className="font-mono text-xs tabular-nums text-foreground/80">
                {s.durationMin}m
              </span>
            </li>
          );
        })}
      </ol>
      <p className="text-[10px] text-muted-foreground mt-2 italic">
        Processing windows free the stylist temporarily — another client may be booked alongside.
      </p>
    </div>
  );
}

function DayView({ date, stylists, bookings, onSelect, onSlotClick, onMove }) {
  const dayBookings = bookings.filter((b) => {
    return isSameDay(b.start_at, date) && b.status !== "cancelled";
  });

  // ---- Drag state ----
  // Native pointer events. We deliberately avoid a DnD library: the
  // grid is already absolutely-positioned with known HOUR_PX +
  // DAY_START_HOUR constants, so hit-testing the drop target reduces
  // to a coordinate read on an element ref. Backend remains the
  // single source of truth — we only compute a proposed
  // (stylist_id, start_at) here.
  const gridRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  // Keep a ref to the latest drag state so window pointer handlers
  // (mounted once when drag becomes truthy) always see the freshest
  // value — without this, the closure captures the initial
  // `moved:false` snapshot and the drop is never recognised.
  useEffect(() => { dragRef.current = drag; }, [drag]);
  // drag shape (when active):
  //   {
  //     booking,           // original booking object
  //     pointerX, pointerY,
  //     snappedTopPx,      // ghost top within the column
  //     snappedMinutes,    // minutes from day start
  //     targetStylistId,   // resolved drop column (may be null mid-air)
  //     moved,             // did the cursor travel >threshold px?
  //   }

  const SNAP_MIN_LOCAL = SNAP_MIN;        // re-exported for in-component readability
  const DRAG_THRESHOLD = DRAG_THRESHOLD_PX;

  const resolveTarget = (clientX, clientY) => {
    if (!gridRef.current) return null;
    // elementsFromPoint scans the stack — find the first ancestor that
    // is a stylist column (data-col-stylist attribute set below).
    const stack = document.elementsFromPoint(clientX, clientY) || [];
    let col = null;
    for (const el of stack) {
      if (el.dataset && el.dataset.colStylist) { col = el; break; }
    }
    if (!col) return null;
    const rect = col.getBoundingClientRect();
    const snappedMinutes = resolveSnapMinutes(rect, clientY, HOUR_PX, HOURS);
    if (snappedMinutes === null) return null;
    // Legality hint — purely a DOM-attribute hit-test. NOT a
    // scheduling decision; backend still validates on drop.
    const overOccupied = legalityHintOverOccupied(stack, dragRef.current?.booking?.id);
    return {
      stylistId: col.dataset.colStylist,
      snappedMinutes,
      snappedTopPx: (snappedMinutes / 60) * HOUR_PX,
      overOccupied,
    };
  };

  const onDragStart = (booking, event) => {
    event.preventDefault();
    if (!isDraggable(booking)) return; // defensive — BookingBlock also gates
    const { clientX, clientY } = event;
    const initial = {
      booking,
      pointerX: clientX,
      pointerY: clientY,
      startX: clientX,
      startY: clientY,
      moved: false,
      target: null,
    };
    dragRef.current = initial;
    setDrag(initial);
    // Body class suppresses text selection + sets grabbing cursor
    // globally so the user can drag past the original block without
    // the cursor flickering back to default mid-drag.
    document.body.classList.add("calendar-dragging");
  };

  useEffect(() => {
    if (!drag) return undefined;
    const onMoveEvent = (e) => {
      const cur = dragRef.current;
      if (!cur) return;
      const moved = cur.moved || crossedDragThreshold(cur.startX, cur.startY, e.clientX, e.clientY);
      const target = moved ? resolveTarget(e.clientX, e.clientY) : null;
      setDrag((prev) => prev && ({
        ...prev,
        pointerX: e.clientX,
        pointerY: e.clientY,
        moved,
        target,
      }));
    };
    const onUpEvent = (e) => {
      const current = dragRef.current;
      setDrag(null);
      dragRef.current = null;
      // Always release the body-level drag class regardless of outcome.
      document.body.classList.remove("calendar-dragging");
      if (!current?.moved) return; // pure click — handled by onClick
      // Suppress the click that fires after pointerup on the same button.
      e.preventDefault();
      e.stopPropagation();
      const target = resolveTarget(e.clientX, e.clientY);
      if (!target) return; // dropped outside the grid → snap back
      const startAtIso = formatDropIso(isoDate(date), DAY_START_HOUR, target.snappedMinutes);
      onMove({
        bookingId: current.booking.id,
        startAtIso,
        stylistId: target.stylistId,
        originalBooking: current.booking,
      });
    };
    window.addEventListener("pointermove", onMoveEvent);
    window.addEventListener("pointerup", onUpEvent, true);
    return () => {
      window.removeEventListener("pointermove", onMoveEvent);
      window.removeEventListener("pointerup", onUpEvent, true);
    };
    // Only re-bind when a new drag session starts (booking id changes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.booking?.id]);

  // Suppress the click that fires AFTER a successful drag's pointerup.
  // We swallow it by checking the moved flag in the onSelect wrapper.
  const onBookingClick = (b) => {
    if (dragRef.current?.moved) return;
    onSelect(b);
  };

  // Ghost overlay anchored to the resolved target column when present.
  const ghost = drag?.moved && drag.target ? (() => {
    const duration = drag.booking.duration_minutes || 30;
    const height = (duration / 60) * HOUR_PX;
    const hh = String(Math.floor(drag.target.snappedMinutes / 60) + DAY_START_HOUR).padStart(2, "0");
    const mm = String(drag.target.snappedMinutes % 60).padStart(2, "0");
    return {
      ...drag.target,
      height,
      label: `${hh}:${mm}`,
      customerName: drag.booking.customer?.name,
      overOccupied: drag.target.overOccupied,
    };
  })() : null;

  return (
    <div className="surface-card overflow-hidden">
      <div className="overflow-auto scrollbar-thin" ref={gridRef}>
        <div
          className="grid"
          style={{
            gridTemplateColumns: `60px repeat(${stylists.length || 1}, minmax(220px, 1fr))`,
          }}
        >
          {/* Header row */}
          <div className="border-b border-r border-border bg-secondary/40 sticky top-0 z-10" />
          {stylists.map((s) => (
            <div
              key={s.id}
              className="border-b border-r border-border bg-secondary/40 p-3 sticky top-0 z-10"
              data-testid={`day-col-header-${s.id}`}
            >
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                <span className="font-medium text-sm">{s.name}</span>
              </div>
            </div>
          ))}

          {/* Time column + day grid */}
          <div className="relative" style={{ height: HOURS * HOUR_PX }}>
            {Array.from({ length: HOURS + 1 }).map((_, i) => (
              <div
                key={i}
                className="absolute left-0 right-0 text-[10px] text-muted-foreground -translate-y-2 pl-2 font-mono"
                style={{ top: i * HOUR_PX }}
              >
                {String(DAY_START_HOUR + i).padStart(2, "0")}:00
              </div>
            ))}
          </div>
          {stylists.map((s) => {
            const colBookings = dayBookings.filter((b) => b.stylist_id === s.id);
            const lanes = assignLanes(colBookings);
            const isDropTarget = ghost?.stylistId === s.id;
            const overOccupied = isDropTarget && ghost?.overOccupied;
            return (
            <div
              key={s.id}
              className={`relative border-l border-border ${
                isDropTarget
                  ? overOccupied
                    ? "bg-[#EADCD9]/30"
                    : "bg-[#D1E0D7]/30"
                  : ""
              }`}
              style={{ height: HOURS * HOUR_PX }}
              data-testid={`day-col-${s.id}`}
              data-col-stylist={s.id}
              data-lane-count={
                colBookings.reduce((m, b) => Math.max(m, lanes.get(b.id)?.total || 1), 1)
              }
            >
              {Array.from({ length: HOURS }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => onSlotClick(s.id, DAY_START_HOUR + i)}
                  className="absolute left-0 right-0 border-t border-border hover:bg-secondary/50 transition-colors"
                  style={{ top: i * HOUR_PX, height: HOUR_PX }}
                  data-testid={`slot-${s.id}-${DAY_START_HOUR + i}`}
                  aria-label={`Add booking ${String(DAY_START_HOUR + i).padStart(2, "0")}:00`}
                />
              ))}
              {colBookings.map((b) => {
                const top = (minutesFromDayStart(b.start_at) / 60) * HOUR_PX;
                const height = ((b.duration_minutes || 30) / 60) * HOUR_PX;
                if (top + height < 0 || top > HOURS * HOUR_PX) return null;
                const ln = lanes.get(b.id) || { lane: 0, total: 1 };
                return (
                  <BookingBlock
                    key={b.id}
                    booking={b}
                    stylistColor={s.color}
                    hourPx={HOUR_PX}
                    top={top}
                    heightPx={height}
                    laneIdx={ln.lane}
                    laneTotal={ln.total}
                    isDone={b.status === "completed"}
                    isDraggingThis={drag?.booking?.id === b.id && drag?.moved}
                    onClick={onBookingClick}
                    onDragStart={onDragStart}
                  />
                );
              })}
              {/* Ghost preview block — only on the resolved drop column.
                  When the cursor sits over another booking's occupied
                  stage we paint a red border + warning column tint as
                  a visual hint. Backend still validates on drop. */}
              {isDropTarget && (
                <div
                  className={`absolute left-1 right-1 rounded-md border-2 border-dashed pointer-events-none flex items-start px-2 py-1.5 z-30 shadow-lg transition-colors duration-100 ${
                    ghost.overOccupied
                      ? "border-[#C46B54] bg-[#EADCD9]/70"
                      : "border-[#1A3626] bg-[#D1E0D7]/70"
                  }`}
                  style={{ top: ghost.snappedTopPx, height: Math.max(28, ghost.height - 2) }}
                  data-testid="drag-ghost"
                  data-ghost-stylist={ghost.stylistId}
                  data-ghost-minutes={ghost.snappedMinutes}
                  data-ghost-over-occupied={ghost.overOccupied ? "true" : "false"}
                >
                  <div className="text-xs">
                    <div
                      className={`font-mono font-medium ${
                        ghost.overOccupied ? "text-[#7A3A2A]" : "text-[#1A3626]"
                      }`}
                    >
                      {ghost.label}
                    </div>
                    <div
                      className={`text-[10px] truncate ${
                        ghost.overOccupied ? "text-[#7A3A2A]/80" : "text-[#1A3626]/70"
                      }`}
                    >
                      {ghost.customerName || "Booking"}
                    </div>
                    {ghost.overOccupied && (
                      <div className="text-[9px] font-mono uppercase tracking-wide text-[#7A3A2A] mt-0.5">
                        likely conflict
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function WeekView({ start, stylists, bookings, onSelect, onDayClick }) {
  const days = Array.from({ length: 7 }).map((_, i) => addDays(start, i));
  return (
    <div className="surface-card overflow-hidden">
      <div className="grid grid-cols-7 border-b border-border">
        {days.map((d) => (
          <button
            key={d.toISOString()}
            onClick={() => onDayClick(d)}
            className="p-3 text-left border-r border-border last:border-r-0 hover:bg-secondary/40 transition-colors"
            data-testid={`week-day-${fmtDate(d)}`}
          >
            <div className="overline">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
            <div className="font-heading text-2xl tracking-tight mt-1">{d.getDate()}</div>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-7 min-h-[420px]">
        {days.map((d) => {
          const list = bookings
            .filter((b) => b.status !== "cancelled" && isSameDay(b.start_at, d))
            .sort((a, b) => isoOrder(a.start_at) - isoOrder(b.start_at));
          return (
            <div key={d.toISOString()} className="p-2 border-r border-border last:border-r-0 space-y-1.5">
              {list.length === 0 && <div className="text-xs text-muted-foreground p-2">—</div>}
              {list.map((b) => {
                const sty = stylists.find((s) => s.id === b.stylist_id);
                return (
                  <button
                    key={b.id}
                    onClick={() => onSelect(b)}
                    data-testid={`week-booking-${b.id}`}
                    className="w-full text-left px-2 py-1.5 rounded-md text-xs border bg-[#F0EDE5] hover:shadow-md transition-all"
                    style={{ borderColor: sty?.color || "#1A3626" }}
                  >
                    <div className="font-mono text-[10px]">{fmtTime(b.start_at)}</div>
                    <div className="font-medium truncate">{b.customer?.name || "—"}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{b.service?.name}</div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
